import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authAPI, novelAPI, genreAPI } from '../services/api';

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      setUser: (user, token) => {
        set({ user, token, isAuthenticated: !!user });
        if (token) localStorage.setItem('token', token);
      },

      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authAPI.login({ email, password });
          // Hỗ trợ cả { token, user } lẫn { data: { token, user } }
          const payload = response?.data ?? response;
          const { user, token } = payload;
          set({ user, token: token ?? null, isAuthenticated: true, isLoading: false });
          if (token) localStorage.setItem('token', token);
          return { success: true };
        } catch (error) {
          set({ isLoading: false, error: error.message });
          return { success: false, message: error.message };
        }
      },

      register: async (username, email, password) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authAPI.register({ username, email, password });
          const payload = response?.data ?? response;
          const { user, token } = payload;
          set({ user, token: token ?? null, isAuthenticated: true, isLoading: false });
          if (token) localStorage.setItem('token', token);
          return { success: true };
        } catch (error) {
          set({ isLoading: false, error: error.message });
          return { success: false, message: error.message };
        }
      },

      logout: async () => {
        try { await authAPI.logout(); } catch (e) { console.error('Logout error:', e); }
        set({ user: null, token: null, isAuthenticated: false });
        localStorage.removeItem('token');
      },

      // Chỉ gọi khi chưa có session (không logged in)
      fetchUser: async () => {
        set({ isLoading: true });
        try {
          const response = await authAPI.getMe();
          const user = response?.data ?? response;
          set({ user, isAuthenticated: true, isLoading: false });
        } catch (error) {
          set({ user: null, token: null, isAuthenticated: false, isLoading: false });
          localStorage.removeItem('token');
        }
      },

      // Gọi khi API thực tế trả 401 (session thực sự hết hạn)
      forceLogout: () => {
        set({ user: null, token: null, isAuthenticated: false });
        localStorage.removeItem('token');
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      },

      updatePreferences: async (preferences) => {
        try {
          const response = await authAPI.updatePreferences(preferences);
          set((state) => ({ user: { ...state.user, preferences: response.data } }));
          return { success: true };
        } catch (error) { return { success: false, message: error.message }; }
      },

      addBookmark: async (novelId) => {
        try {
          await authAPI.addBookmark(novelId);
          const { user } = get();
          set({ user: { ...user, bookmarks: [...(user.bookmarks || []), { novel: novelId, addedAt: new Date() }] } });
          return { success: true };
        } catch (error) { return { success: false, message: error.message }; }
      },

      removeBookmark: async (novelId) => {
        try {
          await authAPI.removeBookmark(novelId);
          const { user } = get();
          set({ user: { ...user, bookmarks: user.bookmarks.filter((b) => b.novel !== novelId && b.novel?._id !== novelId) } });
          return { success: true };
        } catch (error) { return { success: false, message: error.message }; }
      },

      isBookmarked: (novelId) => {
        const { user } = get();
        if (!user?.bookmarks) return false;
        return user.bookmarks.some((b) => b.novel === novelId || b.novel?._id === novelId);
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user, token: state.token, isAuthenticated: state.isAuthenticated }),
    }
  )
);

export const useNovelStore = create((set) => ({
  novels: [], hotNovels: [], featuredNovels: [], recentNovels: [],
  currentNovel: null, chapters: [], currentChapter: null,
  pagination: null, isLoading: false, error: null,

  fetchNovels: async (params = {}) => {
    set({ isLoading: true, error: null });
    try { const r = await novelAPI.getAll(params); set({ novels: r.data, pagination: r.pagination, isLoading: false }); }
    catch (e) { set({ error: e.message, isLoading: false }); }
  },
  fetchHotNovels: async (limit = 10) => {
    try { set({ hotNovels: (await novelAPI.getHot({ limit })).data }); } catch (e) { console.error(e); }
  },
  fetchFeaturedNovels: async () => {
    try { set({ featuredNovels: (await novelAPI.getFeatured()).data }); } catch (e) { console.error(e); }
  },
  fetchRecentNovels: async (limit = 20) => {
    try { set({ recentNovels: (await novelAPI.getRecent({ limit })).data }); } catch (e) { console.error(e); }
  },
  fetchNovel: async (slug) => {
    set({ isLoading: true, error: null });
    try { const r = await novelAPI.getBySlug(slug); set({ currentNovel: r.data.novel, isLoading: false }); return r.data; }
    catch (e) { set({ error: e.message, isLoading: false }); throw e; }
  },
  fetchChapters: async (slug, params = {}) => {
    set({ isLoading: true });
    try { const r = await novelAPI.getChapters(slug, params); set({ chapters: r.data.chapters, pagination: r.pagination, isLoading: false }); return r.data; }
    catch (e) { set({ error: e.message, isLoading: false }); }
  },
  searchNovels: async (query, params = {}) => {
    set({ isLoading: true, error: null });
    try { const r = await novelAPI.search({ q: query, ...params }); set({ novels: r.data, pagination: r.pagination, isLoading: false }); return r; }
    catch (e) { set({ error: e.message, isLoading: false }); }
  },
  clearCurrentNovel: () => set({ currentNovel: null, chapters: [] }),
}));

export const useGenreStore = create((set) => ({
  genres: [], popularGenres: [], currentGenre: null, isLoading: false,
  fetchGenres: async () => {
    set({ isLoading: true });
    try { set({ genres: (await genreAPI.getAll()).data, isLoading: false }); }
    catch (e) { set({ isLoading: false }); console.error(e); }
  },
  fetchPopularGenres: async (limit = 10) => {
    try { set({ popularGenres: (await genreAPI.getPopular({ limit })).data }); } catch (e) { console.error(e); }
  },
}));

export const useReadingStore = create(
  persist(
    (set) => ({
      fontSize: 18, lineHeight: 1.8, theme: 'sepia', fontFamily: 'default',
      setFontSize: (size) => set({ fontSize: Math.min(32, Math.max(12, size)) }),
      setLineHeight: (h) => set({ lineHeight: Math.min(3, Math.max(1.2, h)) }),
      setTheme: (theme) => set({ theme }),
      setFontFamily: (f) => set({ fontFamily: f }),
      increaseFontSize: () => set((s) => ({ fontSize: Math.min(32, s.fontSize + 2) })),
      decreaseFontSize: () => set((s) => ({ fontSize: Math.max(12, s.fontSize - 2) })),
    }),
    { name: 'reading-preferences' }
  )
);

export const useUIStore = create((set) => ({
  isSidebarOpen: false, isSearchOpen: false, isChapterListOpen: false, isSettingsOpen: false,
  toggleSidebar: () => set((s) => ({ isSidebarOpen: !s.isSidebarOpen })),
  toggleSearch: () => set((s) => ({ isSearchOpen: !s.isSearchOpen })),
  toggleChapterList: () => set((s) => ({ isChapterListOpen: !s.isChapterListOpen })),
  toggleSettings: () => set((s) => ({ isSettingsOpen: !s.isSettingsOpen })),
  closeSidebar: () => set({ isSidebarOpen: false }),
  closeSearch: () => set({ isSearchOpen: false }),
  closeChapterList: () => set({ isChapterListOpen: false }),
  closeSettings: () => set({ isSettingsOpen: false }),
  closeAll: () => set({ isSidebarOpen: false, isSearchOpen: false, isChapterListOpen: false, isSettingsOpen: false }),
}));

export const useDomainStore = create((set, get) => ({
  domain: null, isLoading: true, error: null,
  fetchDomain: async () => {
    try {
      const { domainAPI } = await import('../services/api');
      const domain = (await domainAPI.getCurrent()).data;
      set({ domain, isLoading: false });
      if (domain?.settings) {
        const root = document.documentElement;
        if (domain.settings.primaryColor) {
          root.style.setProperty('--primary', domain.settings.primaryColor);
          root.style.setProperty('--primary-light', adjustColor(domain.settings.primaryColor, 20));
          root.style.setProperty('--primary-dark', adjustColor(domain.settings.primaryColor, -20));
        }
        if (domain.settings.secondaryColor) root.style.setProperty('--secondary', domain.settings.secondaryColor);
        if (domain.settings.customCss) {
          let el = document.getElementById('domain-custom-css');
          if (!el) { el = document.createElement('style'); el.id = 'domain-custom-css'; document.head.appendChild(el); }
          el.textContent = domain.settings.customCss;
        }
        if (domain.settings.customJs) {
          try {
            document.getElementById('domain-custom-js')?.remove();
            const s = Object.assign(document.createElement('script'), { id: 'domain-custom-js', textContent: domain.settings.customJs });
            document.body.appendChild(s);
          } catch (e) { console.error('Custom JS error:', e); }
        }
        if (domain.settings.googleAnalyticsId) loadGA(domain.settings.googleAnalyticsId);
        if (domain.settings.facebookPixelId) loadFBPixel(domain.settings.facebookPixelId);
      }
      if (domain?.favicon) {
        const link = document.querySelector("link[rel~='icon']") || Object.assign(document.createElement('link'), { rel: 'icon' });
        if (!link.parentNode) document.head.appendChild(link);
        link.href = domain.favicon;
      }
      return domain;
    } catch (e) { set({ error: e.message, isLoading: false }); }
  },
  getSEO: () => get().domain?.seo || {},
  getContact: () => get().domain?.contact || {},
}));

function adjustColor(color, amt) {
  const c = (n) => Math.min(255, Math.max(0, n));
  const n = parseInt(color.replace('#', ''), 16);
  return `#${[n >> 16, (n >> 8) & 0xff, n & 0xff].map((v) => c(v + amt).toString(16).padStart(2, '0')).join('')}`;
}
function loadGA(id) {
  if (document.querySelector(`script[src*="googletagmanager"]`)) return;
  document.head.appendChild(Object.assign(document.createElement('script'), { async: true, src: `https://www.googletagmanager.com/gtag/js?id=${id}` }));
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  gtag('js', new Date()); gtag('config', id);
}
function loadFBPixel(id) {
  if (window.fbq) return;
  const fbq = (...a) => fbq.callMethod ? fbq.callMethod(...a) : fbq.queue.push(a);
  Object.assign(fbq, { push: fbq, loaded: true, version: '2.0', queue: [] });
  window.fbq = window._fbq = fbq;
  document.head.appendChild(Object.assign(document.createElement('script'), { async: true, src: 'https://connect.facebook.net/en_US/fbevents.js' }));
  fbq('init', id); fbq('track', 'PageView');
}
